import React, {useState, useEffect, useMemo, useContext, useRef} from 'react';
import {VendorContext} from '../../../context/VendorProvider';
import {WebsiteContext} from '../../../context/WebsiteProvider';
import WebsiteModal from '../../../components/modals/WebsiteModal';
import WebsiteForm from '../../website/WebsiteForm';
import {ReactComponent as FilterIcon} from '../../../assets/icons/filter-two.svg';
import {MdFilterAltOff, MdOpenInNew} from 'react-icons/md';
import {FaExclamationCircle, FaSpinner} from 'react-icons/fa';
import ExportModal from '../../../components/modals/ExportModal';
import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import exportIcon from '../../../assets/icons/export.svg';
import {UserContext} from '../../../context/UserProvider';
import recip from '../../../assets/icons/recip.png';
import TetrisModal from '../../../components/modals/TetrisModal';

const useSortableData = (items, config = null) => {
  const [sortConfig, setSortConfig] = useState(config);

  const sortedItems = useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig !== null && sortConfig.direction !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key]?.toString().toLowerCase().trim() ?? "";
        const bValue = b[sortConfig.key]?.toString().toLowerCase().trim() ?? "";
        return sortConfig.direction === "ascending"
          ? aValue.localeCompare(bValue, "en", {sensitivity: "base"})
          : bValue.localeCompare(aValue, "en", {sensitivity: "base"});
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        direction = "descending";
      } else if (sortConfig.direction === "descending") {
        direction = null;
      }
    }
    setSortConfig({key, direction});
  };

  return {items: sortedItems, requestSort, sortConfig};
};

const headerMappings = {
  Site: 'domainName',
  Status: 'status',
  Licensee: 'licensee',
  'MLS Status': 'mlsStatus',
  Office: 'office',
  'MLS ID': 'mlsId',
  'Member#': 'memberNumber',
  'Start Date': 'startDate',
  'Term Date': 'termDate',
};

const VendorWebsites = ({vendorId, vendorName}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const {getVendorWebsites} = useContext(VendorContext);
  const {
    selectedWebsite,
    setSelectedWebsite,
    setReciprocal,
    refreshWebsites,
    vendorWebsiteData,
    setVendorWebsiteData,
    formKey,
    setFormKey,
    blankFormState,
    setForm,
    setHasHistory,
  } = useContext(WebsiteContext);
  const {setUserPreference, vendorWebsiteColumnVisibility, setVendorWebsiteColumnVisibility} = useContext(UserContext);

  const defaultColumns = [
    {columnName: "domainName", visibility: true, width: 250},
    {columnName: "status", visibility: true, width: 200},
    {columnName: "licensee", visibility: true, width: 170},
    {columnName: "mlsStatus", visibility: true, width: 160},
    {columnName: "office", visibility: true, width: 200},
    {columnName: "mlsId", visibility: true, width: 160},
    {columnName: "memberNumber", visibility: true, width: 160},
    {columnName: "startDate", visibility: true, width: 160},
    {columnName: "termDate", visibility: true, width: 160},
  ];

  const [vendorWebsiteColumnVisibilityState, setVendorWebsiteColumnVisibilityState] = useState(
    Array.isArray(vendorWebsiteColumnVisibility) ? vendorWebsiteColumnVisibility : defaultColumns
  );

  useEffect(() => {
    setVendorWebsiteColumnVisibilityState(
      Array.isArray(vendorWebsiteColumnVisibility) ? vendorWebsiteColumnVisibility : defaultColumns
    );
  }, [vendorWebsiteColumnVisibility]);

  const [isExporting, setIsExporting] = useState(false);
  const [showTetrisModal, setShowTetrisModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const [checkboxStatus, setCheckboxStatus] = useState({});
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    rowKey: true,
    vendor: true,
    domainName: true,
    licensee: true,
    firstName: true,
    lastName: true,
    status: true,
    mlsStatus: true,
    office: true,
    mlsId: true,
    memberNumber: true,
    licenseNumber: true,
    startDate: true,
    termDate: true,
    vendorCode: true,
    reciprocal: true,
    dba: true,
    memberAOR: true,
    broker: {
      firstName: true,
      lastName: true,
      memberNumber: true,
      licenseNumber: true,
      mlsId: true,
      office: true,
      officeId: true,
      status: true,
    },
  });
  const [startDate, setStartDate] = useState('');
  const [termDate, setEndDate] = useState('');
  const exportKeyLabels = {
    rowKey: 'Website ID',
    vendor: 'Vendor',
    domainName: 'Domain Name',
    licensee: 'Licensee',
    firstName: 'First Name',
    lastName: 'Last Name',
    status: 'Status',
    mlsStatus: 'MLS Status',
    office: 'Office',
    mlsId: 'MLS ID',
    memberNumber: 'Member Number',
    licenseNumber: 'License Number',
    startDate: 'Start Date',
    termDate: 'Term Date',
    vendorCode: 'Vendor Code',
    reciprocal: 'Reciprocal',
    dba: 'DBA',
    memberAOR: 'Member AOR',
    broker: {
      firstName: 'Broker First Name',
      lastName: 'Broker Last Name',
      memberNumber: 'Broker Member Number',
      licenseNumber: 'Broker License Number',
      mlsId: 'Broker MLS ID',
      office: 'Broker Office',
      officeId: 'Broker Office ID',
      status: 'Broker Status',
    },
  };

  const iconConfigurations = {
    documentsWaiting: {
      icon: (
        <FaExclamationCircle
          className="doc-waiting-icon mr-2 h-4 w-4 text-orange-400 dark:text-yellow-400"
        />
      ),
      label: 'Documents Waiting',
    },
    hasReciprocal: {
      icon: (
        <img
          src={recip}
          alt="Has Reciprocal"
          className="mr-2 inline h-4 w-4 recip"
        />
      ),
      label: 'Reciprocal',
    },
    showAll: {icon: null, label: 'Show All'},
  };

  const [iconFilters, setIconFilters] = useState({
    documentsWaiting: false,
    hasReciprocal: false,
    showAll: true,
  });

  const [showLegend, setShowLegend] = useState(false);
  const legendRef = useRef(null);
  const dropdownRef = useRef(null);
  const [vendorLinkDisabled, setVendorLinkDisabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 200;

  const [requireDoubleClickOnRow, setRequireDoubleClickOnRow] = useState(false);
  const doubleClickTimeoutId = useRef(null);

  const filteredItems = useMemo(() => {
    let itemsToFilter = [...vendorWebsiteData];
    if (searchTerm) {
      itemsToFilter = itemsToFilter.filter((item) =>
        Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    Object.keys(checkboxStatus).forEach((field) => {
      const filterValues = checkboxStatus[field];
      if (filterValues.length > 0) {
        itemsToFilter = itemsToFilter.filter((item) =>
          filterValues.includes(item[field])
        );
      }
    });

    if (!iconFilters.showAll) {
      itemsToFilter = itemsToFilter.filter((item) => {
        return Object.entries(iconFilters).every(([key, value]) => {
          if (!value) return true;
          if (key === 'hasReciprocal') {
            return item.reciprocal === true;
          }
          return item[key] === true;
        });
      });
    }

    return itemsToFilter;
  }, [vendorWebsiteData, searchTerm, checkboxStatus, iconFilters]);

  const {
    items: sortedItems,
    requestSort,
    sortConfig,
  } = useSortableData(filteredItems);

  const summaryData = useMemo(() => {
    const active = filteredItems.filter(
      (item) => item.status === 'Active'
    ).length;
    const inactive = filteredItems.filter(
      (item) => item.status === 'Inactive'
    ).length;
    const pendingActive = filteredItems.filter(
      (item) => item.status === 'Pending Activation'
    ).length;
    const pendingTerm = filteredItems.filter(
      (item) => item.status === 'Pending Termination'
    ).length;

    return {
      total: filteredItems.length.toLocaleString(),
      active: active.toLocaleString(),
      inactive: inactive.toLocaleString(),
      pendingActive: pendingActive.toLocaleString(),
      pendingTerm: pendingTerm.toLocaleString(),
      pending: (pendingActive + pendingTerm).toLocaleString(),
    };
  }, [filteredItems]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getVendorWebsites(vendorId);
        setVendorWebsiteData(result);
      } catch (error) {
        console.error('Error fetching vendor websites:', error);
      }
    };

    fetchData();
  }, [vendorId, setVendorWebsiteData]);

  const handleDropdownClick = (e, field) => {
    e.stopPropagation();
    if (openDropdownField === field) {
      setOpenDropdownField(null);
    } else {
      setOpenDropdownField(field);
    }
  };

  const handleRowSelection = (id) => {
    setSelectedRows((prevSelected) => {
      const newSelected = prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id];
      return newSelected;
    });
  };

  const handleRowClick = (website) => {
    setVendorLinkDisabled(true);
    if (requireDoubleClickOnRow) {
      return;
    } else {
      setSelectedWebsite(website);
      setIsOpen(true);
      setFormKey(website.id);
    }
  };

  const handleRowDoubleClick = (website) => {
    setSelectedWebsite(website);
    setIsOpen(true);
    setFormKey(website.id);
  };

  const openModal = () => {
    setSelectedWebsite({
      vendor: vendorName,
      vendorId: vendorId,
      rowKey: null,
      domainName: '',
      dba: '',
      mlsStatus: '',
      broker: {
        firstName: '',
        lastName: '',
        memberNumber: '',
        licenseNumber: '',
        mlsId: '',
        office: '',
        officeId: '',
        status: '',
      }
    });
    setFormKey(Math.random());
    setForm(blankFormState);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedWebsite(null);
    setReciprocal(false);
    setFormKey(null);
    setHasHistory(false);
    setVendorLinkDisabled(true);
  };

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedItems.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedItems, currentPage]);

  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 0));

    let exportData = [...sortedItems];

    if (startDate || termDate) {
      const startYear = startDate ? parseInt(startDate, 10) : -Infinity;
      const endYear = termDate ? parseInt(termDate, 10) : Infinity;

      exportData = exportData.filter((item) => {
        const itemYear = item.startDate
          ? new Date(item.startDate).getFullYear()
          : null;
        return (
          (itemYear || itemYear === 0) &&
          itemYear >= startYear &&
          itemYear <= endYear
        );
      });
    }

    try {
      const formattedData = exportData.map((item) => {
        let exportItem = {};
        for (const optionKey of Object.keys(exportOptions)) {
          if (exportOptions[optionKey]) {
            if (optionKey === 'broker' && item.broker) {
              for (const brokerKey of Object.keys(exportOptions.broker)) {
                if (exportOptions.broker[brokerKey]) {
                  const brokerLabel = exportKeyLabels.broker[brokerKey];
                  exportItem[brokerLabel] = item.broker[brokerKey] ?? '';
                }
              }
            } else {
              const label = exportKeyLabels[optionKey];
              exportItem[label] =
                item[optionKey] !== null && item[optionKey] !== undefined
                  ? item[optionKey]
                  : '';
            }
          }
        }
        return exportItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, 'VendorWebsitesTable.xlsx');
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      alert("An error occurred during export. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleColumnToggle = (field) => {
    const newVisibility = vendorWebsiteColumnVisibilityState.map((col) =>
      col.columnName === field ? {...col, visibility: !col.visibility} : col
    );
    setVendorWebsiteColumnVisibilityState(newVisibility);
    setVendorWebsiteColumnVisibility(newVisibility);
    setUserPreference({
      vendorWebsiteColumnVisibility: newVisibility,
    });
  };

  const ColumnsDropdown = () => {
    const isDropdownOpen = openDropdownField === 'columns';

    if (!isDropdownOpen) return null;

    return (
      <div
        ref={dropdownRef}
        className="absolute left-0 z-50 rounded border border-gray-200 bg-white p-3 shadow-lg dark:border-stone-600 dark:bg-stone-700"
        style={{
          top: 'calc(100% + 0.5rem)',
          minWidth: '15rem',
          textAlign: 'left',
        }}
      >
        <div className="flex w-full flex-col space-y-1">
          {vendorWebsiteColumnVisibilityState
            .filter((col) => col.columnName !== 'vendor')
            .map((col, i) => (
              <label
                key={i}
                className="flex items-center space-x-2 p-1"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  checked={col.visibility}
                  onChange={() => handleColumnToggle(col.columnName)}
                />
                <span className="w-full text-sm text-gray-900 dark:text-gray-300">
                  {Object.keys(headerMappings).find(
                    (key) => headerMappings[key] === col.columnName
                  ) || col.columnName}
                </span>
              </label>
            ))}
        </div>
      </div>
    );
  };

  const FilterDropdown = ({field}) => {
    if (field !== openDropdownField) return null;

    let filterOptions = [];

    if (field === 'status') {
      filterOptions = [
        'Active',
        'Inactive',
        'Pending Activation',
        'Pending Termination',
        'Pending Change',
      ];
    } else if (field === 'mlsStatus') {
      filterOptions = ['Active', 'Q Suspension', 'Terminated'];
    }

    const handleFilterOptionClick = (event, field, option) => {
      event.stopPropagation();
      setCheckboxStatus((prevStatus) => {
        const newStatus = {...prevStatus};
        if (!newStatus[field]) newStatus[field] = [];
        if (newStatus[field].includes(option)) {
          newStatus[field] = newStatus[field].filter((item) => item !== option);
        } else {
          newStatus[field].push(option);
        }
        return newStatus;
      });
    };

    const handleResetFilter = (event) => {
      event.stopPropagation();
      setCheckboxStatus((prevStatus) => {
        const newStatus = {...prevStatus};
        delete newStatus[field];
        return newStatus;
      });
      setOpenDropdownField(null);
    };

    return (
      <div
        ref={dropdownRef}
        className="w-1/7 absolute z-50 rounded bg-white p-3 drop-shadow-2xl dark:bg-stone-900"
        style={{
          top: 'calc(100% + 0.1rem)',
        }}
        onClick={() => setOpenDropdownField(null)}
      >
        <div className="flex w-full flex-col space-y-1">
          {filterOptions.map((option, i) => (
            <label
              key={i}
              className="flex items-center space-x-2"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className="form-checkbox"
                checked={checkboxStatus[field]?.includes(option) || false}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterOptionClick(e, field, option);
                }}
              />
              <span className="w-full text-xs text-gray-500 dark:text-gray-300">
                {option}
              </span>
            </label>
          ))}
        </div>
        <div className="justify-left mt-4 flex">
          <button
            className="w-1/2 rounded bg-gray-500 px-2 py-1 font-semibold text-white hover:bg-gray-600"
            onClick={handleResetFilter}
          >
            Reset
          </button>
        </div>
      </div>
    );
  };

  const Legend = () => (
    <div
      ref={legendRef}
      className="legend-tooltip absolute left-0 top-full z-50 mt-2 rounded border border-gray-200 bg-white p-2 shadow-lg dark:border-stone-600 dark:bg-stone-700"
      style={{minWidth: '15rem', textAlign: 'left'}}
    >
      {Object.entries(iconConfigurations).map(([key, {icon, label}]) => (
        <label key={key} className="flex items-center space-x-2 p-1">
          <input
            type="radio"
            name="filterGroup"
            checked={iconFilters[key]}
            onChange={() => toggleFilter(key)}
            className="form-radio rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          />
          <span className="flex items-center text-sm text-gray-900 dark:text-gray-300">
            {icon}
            {label}
          </span>
        </label>
      ))}
    </div>
  );

  const toggleLegend = () => {
    setShowLegend((prev) => !prev);
  };

  const toggleFilter = (filterKey) => {
    setIconFilters((prev) => {
      const resetFilters = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      resetFilters[filterKey] = true;
      return resetFilters;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (legendRef.current &&
          !legendRef.current.contains(event.target) &&
          showLegend) ||
        (dropdownRef.current && !dropdownRef.current.contains(event.target))
      ) {
        setOpenDropdownField(null);
        setShowLegend(false);

        setRequireDoubleClickOnRow(true);
        if (doubleClickTimeoutId.current) {
          clearTimeout(doubleClickTimeoutId.current);
        }
        doubleClickTimeoutId.current = setTimeout(() => {
          setRequireDoubleClickOnRow(false);
        }, 500);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (doubleClickTimeoutId.current) {
        clearTimeout(doubleClickTimeoutId.current);
      }
    };
  }, [showLegend]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setCheckboxStatus({});
    setIsFilterActive(false);
    setIconFilters({
      documentsWaiting: false,
      hasReciprocal: false,
      showAll: true,
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    const hasActiveCheckbox = Object.values(checkboxStatus).some(
      (arr) => arr.length > 0
    );
    const iconFilterActive = Object.values(iconFilters).some(
      (value) => value && !iconFilters.showAll
    );
    const active =
      hasActiveCheckbox || searchTerm.length > 0 || iconFilterActive;
    setIsFilterActive(active);
  }, [checkboxStatus, searchTerm, iconFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, checkboxStatus]);

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  const openDomainInNewTab = (domainName) => {
    let url = domainName;
    if (
      !domainName.startsWith('http://') &&
      !domainName.startsWith('https://')
    ) {
      url = 'http://' + domainName;
    }
    window.open(url, '_blank');
  };

  const visibleColumns = vendorWebsiteColumnVisibilityState.filter(
    (col) => col.visibility && col.columnName !== 'vendor'
  );

  return (
    <div
      className="z-10 mx-6 mb-3 border-gray-200 bg-white shadow dark:bg-stone-800 sm:rounded-lg"
      style={{maxHeight: '70vh'}}
    >
      <div
        className="overflow-x-auto"
        style={{height: 'calc(70vh - 50px)'}}
      >
        <table
          className="min-w-full divide-y divide-gray-200"
          style={{tableLayout: 'fixed'}}
        >
          <thead className="z-9 sticky top-0 select-none bg-gray-200 dark:bg-stone-700">
          <tr>
            <th colSpan="100%">
              <div className="m-2 flex border-b-[1px] border-gray-300 pb-2 dark:border-stone-700">
                <button onClick={openModal} className="primary-small">
                  New Website
                </button>
                <button
                  onClick={() => setShowExportModal(true)}
                  className="neutral-small"
                >
                  <img
                    src={exportIcon}
                    alt="Export"
                    className="mb-1 mr-1 inline-block h-4 w-4"
                  />
                  Export
                </button>
                <input
                  type="text"
                  className="table-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchTerm.trim().toLowerCase() === "tetris:start") {
                      setShowTetrisModal(true);
                      setSearchTerm("");
                    }
                  }}
                  placeholder="Search..."
                />

                {showTetrisModal && (
                  <TetrisModal isOpen={showTetrisModal} onClose={() => setShowTetrisModal(false)}/>
                )}
                <div className="summary ml-4 flex">
                  <div className="group relative">
                    <div style={{width: '100px'}}>
              <span className="summary-label select-none text-sm font-bold text-gray-900 dark:text-gray-100">
                Total: {summaryData.total}
              </span>
                    </div>
                    <div
                      className="summary-content absolute left-0 z-50 mt-2 hidden w-48 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 group-hover:block dark:bg-stone-600 dark:text-gray-300">
                      <div className="select-none py-1">
                <span className="summary-item block px-4 py-2 text-sm">
                  Active: {summaryData.active}
                </span>
                        <span className="summary-item block px-4 py-2 text-sm">
                  Inactive: {summaryData.inactive}
                </span>
                        <span className="summary-item block px-4 py-2 text-sm">
                  Pending: {summaryData.pending}
                </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="filter-icon-container"
                    style={{width: '35px'}}
                  >
                    {isFilterActive && (
                      <MdFilterAltOff
                        className="ml-2 mt-1.5 cursor-pointer text-gray-300"
                        onClick={clearAllFilters}
                      />
                    )}
                  </div>
                  <div className="legend-section relative mb-2 ml-6 flex items-center">
                    <div
                      className="legend-trigger flex cursor-pointer items-center"
                      onClick={toggleLegend}
                    >
              <span className="mr-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                Icons
              </span>
                      <svg
                        className="h-4 w-4 text-gray-900 dark:text-gray-100"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    {showLegend && <Legend/>}
                  </div>
                </div>
                <div className="relative ml-4 flex items-center">
                  <div
                    className="columns-trigger mb-2 ml-4 flex cursor-pointer items-center"
                    onClick={(e) => handleDropdownClick(e, 'columns')}
                  >
            <span className="mr-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Columns
            </span>
                    <svg
                      className="h-4 w-4 text-gray-900 dark:text-gray-100"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {openDropdownField === 'columns' && <ColumnsDropdown/>}
                </div>
              </div>
            </th>
          </tr>
          <tr>
            <th></th>
            {visibleColumns.map((col, index) => {
              const header = Object.keys(headerMappings).find(
                (key) => headerMappings[key] === col.columnName
              );
              const field = col.columnName;
              return (
                <th
                  key={field}
                  className="cursor-pointer px-2 pb-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-200"
                  style={{whiteSpace: 'nowrap', width: col.width || 'auto'}}
                >
                  <div className="flex items-center">
                    <span>{header}</span>
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        requestSort(field);
                      }}
                      className="flex items-center"
                    >
              <span
                className={`sort-icon ml-1 cursor-pointer ${
                  sortConfig?.key === field && sortConfig?.direction === 'ascending'
                    ? "text-white font-bold"
                    : "text-gray-400"
                }`}
                style={{fontSize: '0.6rem'}}
              >
                ▲
              </span>
              <span
                className={`sort-icon cursor-pointer ${
                  sortConfig?.key === field && sortConfig?.direction === 'descending'
                    ? "text-white font-bold"
                    : "text-gray-400"
                }`}
                style={{fontSize: '0.6rem'}}
              >
                ▼
              </span>
            </span>
                    {['status', 'mlsStatus'].includes(field) && (
                      <div
                        className="filter-icon"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FilterIcon
                          className="ml-1 h-2.5 w-2.5 cursor-pointer fill-gray-400 dark:fill-gray-300"
                          onMouseDown={(e) => handleDropdownClick(e, field)}
                        />
                      </div>
                    )}
                    {openDropdownField === field && (
                      <FilterDropdown field={field}/>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-600 dark:bg-stone-800">
          {paginatedItems.length > 0 ? (
            paginatedItems.map((item, index) => (
              <tr
                key={item.rowKey || index}
                className={`hover:bg-gray-100 dark:bg-stone-800 dark:hover:bg-stone-700 ${
                  item.status === 'Inactive'
                    ? 'text-gray-400 dark:text-gray-500'
                    : 'dark:text-gray-300'
                }`}
                onClick={(event) => {
                  if (event.target.type !== 'checkbox') {
                    handleRowClick(item);
                  }
                }}
                onDoubleClick={() => handleRowDoubleClick(item)}
              >
                <td className="w-4 px-2">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(item.rowKey)}
                    onChange={() => handleRowSelection(item.rowKey)}
                  />
                </td>
                {visibleColumns.map((col) => {
                  const field = col.columnName;
                  return (
                    <td
                      key={field}
                      className="whitespace-nowrap px-2 py-4 text-xs"
                      title={item[field]}
                      style={{width: col.width || 'auto', maxWidth: col.width || 'auto'}}
                    >
                      {field === 'status' ? (
                        <span
                          className={`inline-block rounded px-3.4 py-1 text-center ${
                            item[field] === 'Pending Termination'
                              ? 'bg-red-100 text-red-500 dark:bg-red-500 dark:text-white min-w-[129px]'
                              : item[field] === 'Pending Activation'
                                ? 'bg-blue-100 text-blue-500 dark:bg-blue-500 dark:text-white min-w-[129px]'
                                : item[field] === 'Pending Change'
                                  ? 'bg-gray-100 text-gray-500 dark:bg-gray-500 dark:text-white min-w-[129px]'
                                  : ''
                          }`}
                        >
                          {item[field]}
                        </span>
                      ) : field === 'domainName' ? (
                        <div className="flex items-center justify-between">
                            <span className="truncate">
                              {truncateText(item[field], 20)}
                            </span>
                          <div className="ml-2 flex items-center space-x-2">
                            {item.reciprocal === true && (
                              <img
                                title="Reciprocal"
                                src={recip}
                                alt="Reciprocal"
                                className="h-4 w-4 recip"
                              />
                            )}
                            {item.documentsWaiting === true && (
                              <FaExclamationCircle
                                title="Pending Document"
                                className="doc-waiting-icon h-4 w-4 text-orange-400 dark:text-yellow-400"
                              />
                            )}
                            <MdOpenInNew
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDomainInNewTab(item[field]);
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        item[field]
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan="100%"
                className="h-[400px] p-5 text-center text-gray-500"
              >
                No results found.
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>

      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Export Data"
        >
          <div className="flex flex-col space-y-4 py-2 pl-2 pr-16">
            <div className="grid grid-cols-4 gap-4">
              {Object.entries(exportOptions)
                .filter(([key, _]) => key !== 'broker')
                .map(([key, value]) => (
                  <label key={key} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name={key}
                      checked={value}
                      onChange={(e) =>
                        setExportOptions({
                          ...exportOptions,
                          [e.target.name]: e.target.checked,
                        })
                      }
                      className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      disabled={isExporting}
                    />
                    <span className="text-gray-700 dark:text-gray-200">
                {exportKeyLabels[key]}
              </span>
                  </label>
                ))}
            </div>
            <div className="mt-4 grid grid-cols-4 gap-4">
              {Object.entries(exportOptions.broker).map(([key, value]) => (
                <label
                  key={`broker-${key}`}
                  className="flex items-center space-x-2"
                >
                  <input
                    type="checkbox"
                    name={key}
                    checked={value}
                    onChange={(e) =>
                      setExportOptions({
                        ...exportOptions,
                        broker: {
                          ...exportOptions.broker,
                          [e.target.name]: e.target.checked,
                        },
                      })
                    }
                    className="form-checkbox rounded text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    disabled={isExporting}
                  />
                  <span className="text-gray-700 dark:text-gray-200">
              {exportKeyLabels.broker[key]}
            </span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-end space-x-4">
              <label className="block">
          <span className="text-gray-700 dark:text-gray-200">
            From Year:
          </span>
                <input
                  type="number"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 block w-36 rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                  }}
                  disabled={isExporting}
                />
              </label>
              <label className="block">
          <span className="text-gray-700 dark:text-gray-200">
            Until Year:
          </span>
                <input
                  type="number"
                  value={termDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 block w-36 rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
                  placeholder="YYYY"
                  min="1900"
                  max={new Date().getFullYear()}
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                  }}
                  disabled={isExporting}
                />
              </label>
            </div>
            <div className="flex justify-end space-x-2 pt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="rounded bg-gray-300 px-4 py-2 font-bold text-black hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 disabled:opacity-50"
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="rounded bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {isExporting ? (
                  <div className="flex items-center">
                    <FaSpinner className="animate-spin mr-2 h-4 w-4"/>
                    <span>Exporting...</span>
                  </div>
                ) : (
                  "Export"
                )}
              </button>
            </div>
          </div>
        </ExportModal>
      )}

      {sortedItems.length > itemsPerPage && (
        <div className="mt-2 flex flex-wrap justify-center">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`mx-1 my-1 rounded px-3 py-1 ${
              currentPage === 1
                ? 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-400'
                : 'bg-blue-500 text-white dark:bg-indigo-600 dark:text-white'
            }`}
          >
            Previous
          </button>
          {windowWidth > 768 ? (
            Array.from({length: totalPages}, (_, index) => (
              <button
                key={index + 1}
                onClick={() => setCurrentPage(index + 1)}
                className={`mx-1 my-1 rounded px-3 py-1 ${
                  currentPage === index + 1
                    ? 'bg-blue-500 text-white dark:bg-indigo-600 dark:text-white'
                    : 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-400'
                }`}
              >
                {index + 1}
              </button>
            ))
          ) : (
            <span className="mx-2 text-gray-700 dark:text-gray-400">
              {currentPage} of {totalPages}
            </span>
          )}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className={`mx-1 my-1 rounded px-3 py-1 ${
              currentPage === totalPages
                ? 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-400'
                : 'bg-blue-500 text-white dark:bg-indigo-600 dark:text-white'
            }`}
          >
            Next
          </button>
        </div>
      )}

      {isOpen && (
        <WebsiteModal
          website={selectedWebsite}
          onClose={closeModal}
          vendorLinkDisabled={vendorLinkDisabled}
          websiteList={sortedItems}
          initialIndex={selectedWebsite ? sortedItems.findIndex(item => item.id === selectedWebsite.id) : -1}
          isVendorSpecific={true}
        >
          <WebsiteForm
            vendor={selectedWebsite}
            onClose={closeModal}
            key={formKey}
            vendorName={vendorName}
            vendorId={vendorId}
            refreshWebsites={refreshWebsites}
          />
        </WebsiteModal>
      )}
    </div>
  );
};

export default VendorWebsites;